import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ICU from 'i18next-icu'
import { initReactI18next } from 'react-i18next'

import { DEFAULT_LOCALE, NAMESPACES, SUPPORTED_LOCALES, type Locale } from '@/i18n/config'

/**
 * i18next configuration.
 *
 * WHY i18next AND NOT LINGUI / react-intl / PARAGLIDE
 *
 *   1. ICU MessageFormat. Spanish requires gender and plural agreement
 *      that this domain hits constantly — "hijo/hija", "nacido/nacida",
 *      "1 hijo" vs "2 hijos". Simple interpolation cannot express that,
 *      and concatenating fragments produces the broken Spanish every
 *      naively-translated app ships with.
 *
 *   2. It runs outside React. Email templates and notifications
 *      (requirement 3) are generated in Edge Functions where there is no
 *      component tree. i18next is a plain object; react-i18next is a thin
 *      binding over it. Lingui and react-intl are React-first.
 *
 *   3. Lazy namespaces. Our routes are already code-split; translations
 *      split along the same seams, so opening the sign-in page does not
 *      download the People vocabulary.
 *
 *   4. It is the boring choice with the largest ecosystem, which for a
 *      fifty-year project outweighs the smaller bundle Paraglide would
 *      give us today.
 */

// Vite resolves this at build time into a lazy chunk per file, so a
// locale is fetched only when it is actually selected.
const resources = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*/*.json'
)

/**
 * Finds the loader for one locale/namespace pair.
 *
 * Matched on the path SUFFIX rather than by exact key. Whether
 * `import.meta.glob` yields keys relative to this file
 * (`./locales/en-AU/common.json`) or rooted at the project
 * (`/src/i18n/locales/en-AU/common.json`) is a bundler detail that has
 * changed between Vite majors — and when it changes, an exact-key lookup
 * does not throw. It silently returns nothing, every string renders as
 * its own key, and the application still builds and passes its tests.
 * That is exactly the failure this file shipped with until it was first
 * run in a browser.
 */
function findLoader(locale: string, namespace: string) {
  const suffix = `/locales/${locale}/${namespace}.json`
  for (const [key, loader] of Object.entries(resources)) {
    if (key.endsWith(suffix)) return loader
  }
  return undefined
}

async function loadNamespace(locale: string, namespace: string) {
  const loader = findLoader(locale, namespace)
  if (!loader) {
    // Loud in development, silent in production: a grandparent reading a
    // biography should see the fallback language, not a stack trace.
    if (import.meta.env.DEV) {
      const known = Object.keys(resources).join(', ') || '(nothing)'
      console.error(`[i18n] no bundle for ${locale}/${namespace}. Glob resolved: ${known}`)
    }
    return {}
  }

  const module = await loader()
  return module.default
}

export async function initI18n(initialLocale: Locale = DEFAULT_LOCALE) {
  await i18next
    .use(ICU)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: initialLocale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      load: 'currentOnly',

      // DO NOT set `nonExplicitSupportedLngs: true` here.
      //
      // It reads as "let 'en' resolve to 'en-AU'", but it does the
      // opposite: it strips the region from the code being CHECKED
      // ('en-AU' → 'en') and then requires that bare tag to appear in
      // `supportedLngs`. Our supported list is region-qualified, so every
      // supported language tested as unsupported, `toResolveHierarchy`
      // returned [], and every single string in the application rendered
      // as its own key — with no error, no warning, and a clean build.
      //
      // It is also unnecessary: `resolveLocale()` in ./config.ts already
      // maps any BCP-47 tag a browser might send onto a supported locale
      // before i18next is ever given one.

      ns: ['common'],
      defaultNS: 'common',
      fallbackNS: 'common',

      detection: {
        // Requirement 6: an explicit choice always beats the browser.
        // The database value is applied over the top once the profile
        // loads — see LocaleProvider.
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'legacy.locale',
        caches: ['localStorage'],
      },

      interpolation: {
        // React escapes for us; double-escaping mangles apostrophes,
        // which Spanish and Irish surnames are full of.
        escapeValue: false,
      },

      returnNull: false,
      // In development a missing key should be loud. In production it
      // degrades to the key rather than rendering "undefined" to a
      // grandparent reading a biography.
      // `false` rather than `undefined`: exactOptionalPropertyTypes means
      // an explicitly-undefined property is not the same as an absent one,
      // and i18next's own type accepts `false` to mean "no handler".
      saveMissing: import.meta.env.DEV,
      missingKeyHandler: import.meta.env.DEV
        ? (lngs: readonly string[], ns: string, key: string) => {
            console.warn(`[i18n] missing key: ${ns}:${key} (${lngs.join(', ')})`)
          }
        : false,

      react: {
        useSuspense: false,
      },
    })

  await preloadNamespaces(initialLocale)

  // Development-only handle for inspecting the resource store from the
  // browser console. Never exposed in a production bundle.
  if (import.meta.env.DEV) {
    ;(globalThis as unknown as { i18next?: typeof i18next }).i18next = i18next
  }

  return i18next
}

async function preloadNamespaces(locale: string) {
  await Promise.all(
    NAMESPACES.map(async (namespace) => {
      const bundle = await loadNamespace(locale, namespace)
      i18next.addResourceBundle(locale, namespace, bundle, true, true)
    })
  )
}

/**
 * Switches language at runtime.
 *
 * Also updates `<html lang>` and `dir` — not cosmetic: screen readers
 * choose a voice from `lang`, and browsers pick hyphenation rules from
 * it. An archive meant to be read aloud to elderly relatives cannot get
 * this wrong.
 */
export async function changeLocale(locale: Locale) {
  await preloadNamespaces(locale)
  await i18next.changeLanguage(locale)

  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
    document.documentElement.dir = 'ltr'
  }
}

export { i18next }
